import { Flex, useDisclosure } from "@chakra-ui/react";
import React, { forwardRef, useEffect, useState } from "react";
import { components, GroupBase, MenuListProps } from "react-select";
import CreatableSelect from "react-select/creatable";

import { Alert, LoadingIndicator, Select } from "../../../components";
import useCurrentBreakpoint from "../../../hooks/useCurrentBreakpoint";
import useDebounce from "../../../hooks/useDebounce";
import useSelectTheme from "../../../hooks/useSelectTheme";
import {
  PositionListItemFragment,
  usePositionInfoLazyQuery,
} from "../../graphql";
import useCurrentUser from "../../hooks/useCurrentUser";
import { usePositionsPagination } from "../../hooks/usePositionsPagination";
import { AddPositionModal } from "../AddPositionModal";

type PositionOption = {
  label: string;
  value: PositionListItemFragment;
};

const formatOption = (position: PositionListItemFragment): PositionOption => ({
  label: position.displayTitle,
  value: position,
});

type PositionSelectData = {
  loadMorePositions: () => void;
  hasMoreData: boolean;
  isLoadingMore: boolean;
  loading: boolean;
};

const CustomMenuList: React.FC<
  MenuListProps<PositionOption, false, GroupBase<PositionOption>> & {
    selectProps: { data: PositionSelectData };
  }
> = (props) => {
  const { data } = props.selectProps;

  const handleScrollCapture = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;
    // Trigger load more when user has scrolled 90% of the way down
    if (
      scrollPercentage > 0.9 &&
      data.hasMoreData &&
      !data.isLoadingMore &&
      !data.loading
    ) {
      data.loadMorePositions();
    }
  };

  return (
    <components.MenuList
      {...props}
      innerProps={{ onScrollCapture: handleScrollCapture }}
    >
      <>
        {props.children}
        {data.isLoadingMore && (
          <Flex p="4" width="100%" justifyContent="center">
            <LoadingIndicator />
          </Flex>
        )}
      </>
    </components.MenuList>
  );
};

const smallStyles: Partial<Record<string, any>> = {
  control: (provided: Record<string, any>) => ({
    ...provided,
    fontSize: "12px",
    minHeight: "30px",
  }),
  valueContainer: (provided: Record<string, any>) => ({
    ...provided,
    padding: "2px",
  }),
  dropdownIndicator: (provided: Record<string, any>) => ({
    ...provided,
    padding: "4px",
  }),
  clearIndicator: (provided: Record<string, any>) => ({
    ...provided,
    padding: "2px",
  }),
  input: (provided: Record<string, any>) => ({
    ...provided,
    marginLeft: "8px",
  }),
  placeholder: (provided: Record<string, any>) => ({
    ...provided,
    marginLeft: "8px",
  }),
};

type PositionSelectProps = {
  positionId?: string;
  currentPositionId?: string;
  name?: string;
  disabled?: boolean;
  onSelect: (callPosition: PositionListItemFragment | undefined) => void;
  size?: "sm" | "md";
  isClearable?: boolean;
  customStyles?: Record<string, any>;
};

const PositionSelect = forwardRef<any, PositionSelectProps>(
  (
    {
      name,
      positionId,
      currentPositionId,
      disabled,
      onSelect,
      size = "md",
      isClearable = true,
      customStyles,
    },
    ref
  ) => {
    const currentBreakpoint = useCurrentBreakpoint();
    const currentUser = useCurrentUser();
    const [theme, styles] = useSelectTheme(
      size === "sm" ? smallStyles : customStyles
    );
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [title, setTitle] = useState("");
    const [position, setPosition] = useState<PositionListItemFragment>();
    const [searchTerm, setSearchTerm] = useState("");
    const debouncedSearchTerm = useDebounce(searchTerm, 250);

    const {
      positions,
      loading,
      error,
      loadMorePositions,
      hasMoreData,
      isLoadingMore,
    } = usePositionsPagination({
      searchTerm: debouncedSearchTerm,
    });

    // Lazy query to fetch position by ID if not found in paginated list
    // Uses PositionInfoQuery (lightweight) instead of PositionQuery (heavy - loads candidates/callGuides)
    const [fetchPositionById, { loading: fetchingPosition }] =
      usePositionInfoLazyQuery();

    useEffect(() => {
      // Skip if we already have a position with matching ID (e.g., just created via modal)
      if (position?.id === positionId) {
        return;
      }

      // If no positionId provided, clear position
      if (!positionId) {
        setPosition(undefined);
        return;
      }

      // Try to find in paginated positions first
      const foundPosition = positions?.find(
        (p: PositionListItemFragment) => p.id === positionId
      );

      if (foundPosition) {
        setPosition(foundPosition);
      } else if (!loading) {
        // Position not in paginated list and pagination is done loading
        // Fetch the position by ID directly
        fetchPositionById({ variables: { id: positionId } }).then((result) => {
          if (result.data?.position) {
            const pos = result.data.position;
            // Convert to PositionListItemFragment shape
            setPosition({
              id: pos.id,
              title: pos.title,
              createdAt: pos.createdAt,
              displayTitle: pos.displayTitle,
              client: pos.client,
            });
          }
        });
      }
    }, [positionId, positions, position?.id, loading, fetchPositionById]);

    let positionOptions:
      | { label: string; value: PositionListItemFragment }[]
      | undefined = [];
    if (currentPositionId) {
      positionOptions = positions?.reduce((acc, position) => {
        if (position.id !== currentPositionId) {
          acc.push(formatOption(position));
        }
        return acc;
      }, [] as { label: string; value: PositionListItemFragment }[]);
    } else {
      positionOptions = positions?.map(formatOption);
    }

    const props = {
      ref,
      name,
      theme,
      styles,
      isClearable,
      menuShouldBlockScroll: true,
      menuShouldScrollIntoView: true,
      menuPlacement: "auto" as const,
      isDisabled: disabled,
      isLoading: loading || fetchingPosition,
      value: position ? formatOption(position) : undefined,
      options: positionOptions,
      onInputChange: setSearchTerm,
      onChange: (option: any) => {
        const value = (
          option as {
            value: PositionListItemFragment;
          }
        )?.value;
        onSelect(value);
      },
      menuPortalTarget: document.getElementById("root"),
      components: {
        MenuList: CustomMenuList as React.FC<
          MenuListProps<PositionOption, false, GroupBase<PositionOption>>
        >,
      },
      data: {
        loadMorePositions,
        hasMoreData,
        isLoadingMore,
        loading,
      },
    };
    return (
      <>
        <AddPositionModal
          title={title}
          isOpen={isOpen}
          onClose={onClose}
          onSave={(position) => {
            setPosition(position);
            onSelect(position);
            setTitle("");
            onClose();
          }}
        />
        {error ? (
          <Alert status="error" description="Error loading positions" />
        ) : currentUser.userRole?.canCreatePosition ? (
          <CreatableSelect
            aria-label="position-select"
            placeholder={
              currentBreakpoint === "base"
                ? "Select or create position..."
                : "Select or create new position..."
            }
            onCreateOption={(title) => {
              setTitle(title);
              onOpen();
            }}
            {...props}
          />
        ) : (
          <Select
            aria-label="position-select"
            placeholder="Select Position..."
            {...props}
          />
        )}
      </>
    );
  }
);

PositionSelect.displayName = "PositionSelect";
export default PositionSelect;
